import React, { useEffect } from 'react';
import {
  FaRocket,
  FaPenFancy,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaRedditAlien,
  FaCommentDots,
  FaYoutube,
  FaMediumM,
  FaDev,
  FaMagic,
  FaHashtag,
  FaLayerGroup,
  FaBolt
} from 'react-icons/fa';
import SeoHelmet from './SeoHelmet';
import MonoButton from '../kits/DesignKit/MonoButton';
import { ZenPostHeroSection, ZenPostWorkflowSection, ZenPostPricingSection, ZenPostOllamaSection } from './ZenPostSections';

const ZenPostStudioLandingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trackCTA = (ctaName) => {
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'ZenPostStudio',
        event_label: ctaName
      });
    }
    console.log(`CTA Clicked: ${ctaName}`);
  };

  const stats = [
    { label: 'Plattformen', value: '9+' },
    { label: 'Zeit gespart', value: '100%' },
    { label: 'Mehr Reichweite', value: '10x' },
    { label: 'Posts erstellt', value: '1M+' }
  ];

  const platforms = [
    { name: 'Facebook', description: 'Long-Form Posts mit optimalem Format', icon: <FaFacebookF /> },
    { name: 'X (Twitter)', description: 'Thread-Support mit Zeichen-Optimierung', icon: <FaTwitter /> },
    { name: 'LinkedIn', description: 'Business-Ton & klare Struktur', icon: <FaLinkedinIn /> },
    { name: 'Instagram', description: 'Captions & Hashtags für Reichweite', icon: <FaInstagram /> },
    { name: 'Reddit', description: 'TL;DR & Subreddit-Vorschläge', icon: <FaRedditAlien /> },
    { name: 'Threads', description: 'Conversation-Ready Posts', icon: <FaCommentDots /> },
    { name: 'YouTube', description: 'Titel, Beschreibung & Shorts-Teaser', icon: <FaYoutube /> },
    { name: 'Medium', description: 'Publizieren mit passender Struktur', icon: <FaMediumM /> },
    { name: 'dev.to', description: 'Developer-Optimierung inklusive Tags', icon: <FaDev /> }
  ];

  const features = [
    {
      title: 'One-Click Conversion',
      description: 'Wandle jeden Artikel sofort in Social Posts um.',
      icon: <FaBolt />
    },
    {
      title: 'Plattform-Optimiert',
      description: 'Automatische Anpassung an Ton, Länge & Format.',
      icon: <FaLayerGroup />
    },
    {
      title: 'Smart Hashtags',
      description: 'Relevante Hashtags für maximale Sichtbarkeit.',
      icon: <FaHashtag />
    },
    {
      title: 'Thread Support',
      description: 'Erstelle X/Threads-Serien mit einem Klick.',
      icon: <FaMagic />
    }
  ];

  const workflowSteps = [
    {
      title: 'Artikel schreiben oder importieren',
      description: 'Nutze ZenEditor oder importiere bestehende Blogposts.'
    },
    {
      title: 'Plattformen auswählen',
      description: 'Wähle die Kanäle für deine Inhalte in Sekunden.'
    },
    {
      title: 'Posts generieren',
      description: 'KI erstellt Plattform-optimierte Varianten automatisch.'
    },
    {
      title: 'Feinschliff & teilen',
      description: 'Passe Details an, kopiere oder publiziere direkt.'
    }
  ];

  const pricingPlans = [
    {
      name: 'ZenEditor',
      price: 'Kostenlos',
      emoji: '✍️',
      icon: <FaPenFancy className="text-brand" />,
      description: 'Reines Schreiben im Web oder als Desktop-App',
      features: [
        'Reines Schreiben (Web oder Desktop)',
        'Lokale KI mit Ollama (optional)',
        'Lokale Datenspeicherung',
        'Kein Publisher',
        'Kein Doc Studio',
        '100% Offline',
        'Unbegrenzte Nutzung'
      ],
      cta: 'Live Demo starten',
      popular: false,
      ctaAction: () => {
        trackCTA('Pricing_Live_Demo');
        window.location.href = '/dashboard?demo=zeneditor';
      }
    },
    {
      name: 'ZenPost Studio Pro',
      price: '39€',
      period: '/Monat',
      emoji: '🚀',
      icon: <FaRocket className="text-brand" />,
      description: 'Web + Desktop + Cloud-Sync + Doc Studio',
      features: [
        'Alles aus Desktop',
        'Cloud-Sync über alle Geräte',
        'Team-Kollaboration (bis 5 User)',
        'Analytics & Insights',
        'Custom Templates',
        'Priority Support',
        'Versionierung & History',
        'API-Zugriff (Basic)',
        'Doc Studio',
        'KI Anbindung an andere KI möglich'
      ],
      cta: 'Pro starten',
      popular: true,
      ctaAction: () => {
        trackCTA('Pricing_Pro_Start');
        window.location.href = 'mailto:saghallo@denisbitter.de?subject=ZenPost Studio Pro Plan';
      }
    }
  ];

  const faqs = [
    {
      q: 'Welche Plattformen unterstützt ZenPost Studio?',
      a: 'Aktuell optimiert für LinkedIn, X, Instagram, Reddit, Threads und Facebook. Weitere Plattformen folgen.'
    },
    {
      q: 'Kann ich die generierten Posts anpassen?',
      a: 'Ja. Du kannst jede Version bearbeiten, kürzen oder erweitern, bevor du sie veröffentlichst.'
    },
    {
      q: 'Wie funktioniert die lokale KI?',
      a: 'ZenEditor nutzt Ollama, damit deine Texte lokal auf deinem Gerät verarbeitet werden. Keine Cloud, keine API-Kosten.'
    },
    {
      q: 'Ist ZenEditor wirklich kostenlos?',
      a: 'Ja. ZenEditor bleibt dauerhaft kostenlos und ist komplett offline nutzbar.'
    },
    {
      q: 'Kann ich mehrere Artikel auf einmal umwandeln?',
      a: 'Mit ZenPost Studio Pro kannst du mehrere Inhalte parallel vorbereiten und verwalten.'
    }
  ];

  return (
    <>
      <SeoHelmet
        title="ZenPost Studio & ZenEditor – Schreiben für Creator, Blogger & Autoren"
        description="ZenEditor ist kostenlos: reines Schreiben im Web oder Desktop, lokale KI via Ollama, 100% offline. ZenPost Studio Pro ergänzt Cloud-Sync, Team-Features und Doc Studio für Creator, Blogger und Teams."
        keywords="zenpost studio, zeneditor, schreiben, creator, blogger, autoren, offline editor, lokale ki, ollama, markdown editor, editorjs, writing app, doc studio"
        ogImage="/images/zenpoststudio-og.jpg"
      />

      <div
        className="min-h-screen text-white font-mono"
        style={{
          background: 'linear-gradient(180deg, #2a1035 0%, #141629 30%, #0b1020 100%)'
        }}
      >
        <ZenPostHeroSection
          eyebrow="AI-Powered Social Media Tool"
          title="Transform Blog Posts into"
          titleAccent="Viral Social Content"
          subtitle="Verwandle jeden Artikel in plattform-optimierte Posts für 9+ Kanäle wie LinkedIn, X, Instagram, Reddit und Threads – in Sekunden."
          primaryCtaText="Jetzt Live Demo starten"
          onPrimaryCta={() => {
            trackCTA('Hero_Live_Demo');
            window.location.href = '/dashboard?demo=zeneditor';
          }}
          secondaryCtaText="Unterstützte Plattformen"
          onSecondaryCta={() => {
            document.getElementById('platforms')?.scrollIntoView({ behavior: 'smooth' });
          }}
          primaryUnderline="pink"
          secondaryUnderline="gray"
          stats={stats}
        />

        <section id="platforms" className="py-16 sm:py-20 px-4 bg-[#0b1020]">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center rounded-full border border-brand px-3 py-1 text-xs font-semibold text-slate-300">
              Supported Platforms
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-brand">
              Ein Artikel, neun Plattformen
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-300 max-w-3xl mx-auto">
              Jede Plattform bekommt Inhalte, die zu ihrem Format und ihrer Zielgruppe passen.
            </p>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 text-left">
              {platforms.map((platform) => (
                <div
                  key={platform.name}
                  className="rounded-2xl border border-slate-700/60 bg-slate-950/40 p-6 shadow-lg shadow-slate-950/40"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-800/80 text-brand text-xl">
                    {platform.icon}
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-white">
                    {platform.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300">
                    {platform.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 px-4 bg-[#0d1224]">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center rounded-full border border-brand  px-3 py-1 text-xs font-semibold">
              Features
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold text-brand">
              Alles, was Creator brauchen
            </h2>
            <p className="mt-3 text-base sm:text-lg text-slate-300 max-w-3xl mx-auto">
              Smarte Funktionen, um deine Content-Produktion schneller und konsistenter zu machen.
            </p>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 text-left">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-2xl border border-slate-700/60 bg-slate-950/40 p-6 shadow-lg shadow-slate-950/40"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-fuchsia-500/10 text-brand text-xl">
                    {feature.icon}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-300">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ZenPostOllamaSection
          badge="Killer Feature: Ollama"
          title="KI lokal auf deinem Gerät"
          subtitle="Lokale KI ohne Cloud. Schneller, günstiger, privater – der Zen-Kern deines Workflows."
          howItWorksTitle="So arbeitet die lokale KI"
          howItWorksDescription="Installiere Ollama einmal. ZenPost Studio erkennt es automatisch. Modelle laufen lokal auf deinem Mac/Windows/Linux – ohne API-Kosten."
          theme="dark"
        />

        <section className="bg-[#0b1020]">
          <ZenPostWorkflowSection
            title="Vier einfache Schritte"
            subtitle="Von Blogpost zu Social Content in Minuten"
            steps={workflowSteps}
            variant="timeline"
          />
        </section>

        <ZenPostPricingSection
          title="Einfaches, transparenter Preis"
          subtitle="Starte kostenlos, wachse mit deinem Content-Team."
          plans={pricingPlans}
          onCTAClick={(plan) => trackCTA(`Pricing_${plan.name.replace(/\s+/g, '_')}`)}
          showEnterpriseContact={true}
          theme="dark"
        />

        <section className="py-16 sm:py-20 px-4 bg-[#0b1020]">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center rounded-full border border-brand bg-slate-900/60 px-3 py-1 text-xs font-semibold text-slate-300">
              FAQ
            </div>
            <h2 className="mt-4 text-3xl text-brand sm:text-4xl md:text-5xl font-extrabold">
              Häufige Fragen
            </h2>

            <div className="mt-10 space-y-4 text-left">
              {faqs.map((item) => (
                <details
                  key={item.q}
                  className="rounded-2xl border border-slate-700/60 bg-slate-950/40 p-5"
                >
                  <summary className="cursor-pointer text-base sm:text-lg font-semibold text-white">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-sm sm:text-base text-slate-300">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-[32px] border border-brand p-8 sm:p-12 text-center shadow-2xl shadow-fuchsia-500/20">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-fuchsia-200/20 text-brand text-xl">
                <FaRocket />
              </div>
              <h2 className="mt-6 text-3xl sm:text-4xl font-extrabold text-brand">
                Ready to 10x your social presence?
              </h2>
              <p className="mt-4 text-base sm:text-lg text-slate-300">
                Tausende Creator sparen jede Woche Stunden mit ZenPost Studio.
              </p>
              <MonoButton
                onClick={() => {
                  trackCTA('Final_CTA');
                  window.location.href = '/dashboard?demo=zeneditor';
                }}
                className="mt-8 rounded-full border-transparent bg-gradient-to-r from-fuchsia-500 to-violet-500 px-8 py-3 text-sm sm:text-base font-semibold text-white shadow-lg shadow-fuchsia-500/30"
                underlineColors="white"
              >
                Jetzt Live Demo starten
              </MonoButton>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ZenPostStudioLandingPage;
